import { EAccountStatus, LOGGED_IN_STATUS, type IUserSession } from '@/service/auth/types';
import { z } from 'zod';
import { createTabChannel } from './tabChannel';

/** 本页持有的会话为何不再有效。编辑器据此选提示文案 */
export enum EAuthEndReason {
  /** 用户主动登出 */
  SignedOut = 'signed-out',
  /** 会话过期，或被其他设备登录挤掉 */
  Expired = 'expired',
  /** 账号被停用 */
  Deactivated = 'deactivated',
  /** 账号被删除 */
  Deleted = 'deleted',
  /** 本浏览器登录了新会话，本页持有的已不是活跃会话 */
  Superseded = 'superseded'
}

/**
 * 账号被管理员停用或删除。这两种用户无法自行恢复，设计稿给的提示措辞与样式
 * 都与其他终止原因不同（红色警示、正文灰底块、登录页常驻提示）
 */
export const isAccountBlocked = (reason: EAuthEndReason): boolean =>
  reason === EAuthEndReason.Deactivated || reason === EAuthEndReason.Deleted;

/** 跨标签页鉴权只有这一种消息：本页持有的会话已不再有效 */
export interface IAuthSyncMessage {
  reason: EAuthEndReason;
  /**
   * 抢救保存凭证，120s 内可反复使用。整个浏览器只有第一趟 `/auth/status` 拿得到，
   * 所以必须随公告送出去——别的标签页自己再查是查不到的
   */
  salvageToken?: string;
}

const authSyncSchema = z.object({
  reason: z.enum(EAuthEndReason),
  salvageToken: z.string().optional()
});

const authChannel = createTabChannel<IAuthSyncMessage>('slidar:auth', (data) => {
  const result = authSyncSchema.safeParse(data);
  return result.success ? result.data : undefined;
});

/** 返回退订函数。接收方无条件执行，判定已经在 `checkSession` 用服务端的回答做完了 */
export const subscribeAuthSync = authChannel.subscribe;

/**
 * 轮询检出会话已终止。投给本页是必要的：检出方自己不跳转，
 * 跳转与编辑器的确认框都由订阅方执行。
 */
export const publishSessionEnded = (reason: EAuthEndReason, salvageToken?: string) =>
  authChannel.publishToAll({ reason, salvageToken });

/** 用户主动登出。调用方自己就在跳登录页，收到自己的公告只会重复跳一次 */
export const publishSignedOut = () => authChannel.publishToOthers({ reason: EAuthEndReason.SignedOut });

/** 本浏览器登录了新会话。刚登录的这一页自己会导航，收到自己的公告只会盖掉它的跳转目标 */
export const publishSessionSuperseded = () => authChannel.publishToOthers({ reason: EAuthEndReason.Superseded });

/** 本页自查出持有的已不是活跃会话。那是自己的陈旧状态，不是新闻，不投给其他标签页 */
export const publishSelfStale = () => authChannel.publishToSelf({ reason: EAuthEndReason.Superseded });

/** `checkSession` 的结论 */
export enum ESessionCheck {
  /** 本页持有的仍是活跃会话 */
  Ok = 'ok',
  /** 本页还没有 sessionId，记下这一趟的 */
  Seed = 'seed',
  /** 会话仍在，但已不是本页持有的那个 */
  Stale = 'stale',
  /** 会话已终止，无新会话接替 */
  Ended = 'ended'
}

export type TSessionCheck =
  | { check: ESessionCheck.Ok }
  | { check: ESessionCheck.Seed }
  | { check: ESessionCheck.Stale }
  | { check: ESessionCheck.Ended; reason: EAuthEndReason };

/** 未知值一律归 Expired：accountStatus 是外部数据，default 分支同时充当类型收窄 */
function endReasonFromAccountStatus(accountStatus: string | undefined): EAuthEndReason {
  switch (accountStatus) {
    case EAccountStatus.Deactivate:
      return EAuthEndReason.Deactivated;
    case EAccountStatus.Deleted:
      return EAuthEndReason.Deleted;
    default:
      return EAuthEndReason.Expired;
  }
}

/**
 * 会话有效性的唯一判定点：拿本页 sessionId 与服务端这一趟的回答核对，而不是拿另一个
 * 标签页的消息 payload 猜。会话终止时不分辨是谁的——一个浏览器只有一份 cookie。
 */
export function checkSession(session: IUserSession, currentSessionId: string | undefined): TSessionCheck {
  if (session.loginStatus !== LOGGED_IN_STATUS) {
    return { check: ESessionCheck.Ended, reason: endReasonFromAccountStatus(session.accountStatus) };
  }
  if (!currentSessionId) return { check: ESessionCheck.Seed };
  return currentSessionId === session.sessionId ? { check: ESessionCheck.Ok } : { check: ESessionCheck.Stale };
}
